'use client';

/**
 * HomepageV3 — App Download (QR) band.
 *
 * Ported 1:1 from `design-refs/source/private-home.css`
 * (`.se-cta-row`, `.se-cta-app`, `.se-app-bonus`, `.se-app-qr`, `.se-app-badges`,
 * `.se-store-badge`, `.se-play-tri`) and `private-home.js` (`buildBottom` /
 * `storeBadge`).
 *
 * The band sits on the dark night surface above the footer: a bonus headline +
 * sub copy beside the QR code, App Store / Google Play badges, and a fine-print
 * footnote. QR asset lives at `/homepage-v3/app-qr.png`.
 */
import * as React from 'react';
import * as Sentry from 'utils/errorTracking';
import { styled } from '@mui/material/styles';
import { trackEvent, trackEventWithGTM } from 'utils/analytics';

import { withSe3AssetPrefix } from '../assetPrefix';
import { FONTS } from '../theme/tokens';

export interface AppDownloadSectionProps {
  /** Path to the QR image (defaults to the copied public asset). */
  qrSrc?: string;
  /**
   * Show the left "create a free account / sign up to get notified" column
   * (logged-out `.se-cta-row` 2-col layout). When false, renders the solo
   * `.se-cta-row-solo` variant (logged-in). Defaults to true.
   */
  showAccess?: boolean;
  /**
   * Show the right "download the app" column (QR code + store badges). When
   * false the band renders the access column alone, centred across the full
   * width — for surfaces where the StartEngine app is not offered (e.g. Canada).
   * Defaults to true.
   */
  showApp?: boolean;
  /** Access-column headline (supports an `<em>` accent via `accessEm`). */
  accessText?: string;
  /** Italic champagne fragment within the access headline. */
  accessEm?: string;
  /** Access-column CTA label. */
  signupLabel?: string;
  /** Copy closing the access headline after the `<em>` accent. */
  accessTail?: string;
  /** QR-column headline. */
  bonusHead?: React.ReactNode;
  /** QR-column sub copy. */
  bonusSub?: React.ReactNode;
  /**
   * Fine print beneath the store badges. Pass `null` on surfaces where the
   * default US Venture Club trial footnote does not apply (e.g. Canada).
   */
  footnote?: React.ReactNode | null;
  /** Analytics page key, so non-homepage surfaces report their own page. */
  trackingPage?: string;
  /** Access CTA click handler. */
  onSignUpClick?: () => void;
  /** App Store URL for the iOS download badge. */
  appStoreHref?: string;
  /** Google Play URL for the Android download badge. */
  googlePlayHref?: string;
}

const APP_STORE_HREF = 'https://apps.apple.com/us/app/startengine-startup-investing/id1560434961';
const GOOGLE_PLAY_HREF =
  'https://play.google.com/store/apps/details?id=com.startengine.android&hl=en_US';

const APP_QR_SRC = '/homepage-v3/app-qr.png';

const Row = styled('section')({
  background: 'var(--night)',
  borderTop: '1px solid var(--gold)',
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'stretch',
  // 2-column (logged-out) layout: access | app.
  // PD-35996 — the source `.se-cta-row` only goes 2-up above the 1080px tablet
  // breakpoint (it stacks at ≤1080px). Using 769px kept tablet widths
  // (834/1024) side-by-side and compressed, diverging from the handoff.
  '&[data-solo="false"]': {
    '@media (min-width: 1081px)': {
      gridTemplateColumns: '1.05fr 1fr',
    },
    '& .se-cta-app': {
      '@media (min-width: 1081px)': {
        borderLeft: '1px solid rgba(233,215,182,0.16)',
        background: 'linear-gradient(160deg, #103A3E 0%, rgba(7,25,26,0) 72%)',
      },
      '@media (max-width: 1080px)': {
        borderTop: '1px solid rgba(233,215,182,0.16)',
      },
    },
  },
  '&[data-solo="true"] .se-app-bonus': {
    justifyContent: 'space-between',
    maxWidth: 760,
  },
  // No app column: the access copy is the whole band, so it centres rather than
  // hugging the left edge of a half-width column that no longer exists.
  '&[data-app="false"] .se-cta-access': {
    alignItems: 'center',
    textAlign: 'center',
    padding: '72px 56px',
    '@media (max-width: 768px)': {
      padding: '48px var(--pad)',
    },
    '& .se-cta-access-text': {
      maxWidth: 720,
      fontSize: 30,
      '@media (max-width: 768px)': {
        fontSize: 22,
      },
    },
    '& .se-cta-actions': {
      justifyContent: 'center',
    },
  },
});

const Access = styled('div')({
  padding: '64px 56px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '@media (max-width: 768px)': {
    padding: '44px var(--pad)',
  },
});

const AccessText = styled('p')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: 26,
  color: '#fff',
  lineHeight: 1.34,
  maxWidth: 480,
  margin: 0,
  textWrap: 'pretty',
  '& em': {
    fontStyle: 'italic',
    color: 'var(--champagne)',
  },
  '@media (max-width: 768px)': {
    fontSize: 22,
  },
});

const AccessActions = styled('div')({
  marginTop: 28,
  display: 'flex',
  gap: 12,
});

const SignupBtn = styled('button')({
  fontFamily: FONTS.sans,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'var(--night)',
  background: 'var(--champagne)',
  border: 'none',
  padding: '15px 30px',
  borderRadius: 14,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background 0.15s',
  '&:hover': { background: '#f3e6cb' },
});

const App = styled('div')({
  padding: '64px 56px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '@media (max-width: 768px)': {
    padding: '44px var(--pad)',
  },
});

const Bonus = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 24,
  marginTop: 0,
});

const BonusHead = styled('div')({
  fontFamily: FONTS.serif,
  fontWeight: 400,
  fontSize: 26,
  lineHeight: 1.2,
  color: '#fff',
  letterSpacing: '-0.01em',
});

const BonusSub = styled('p')({
  fontFamily: FONTS.sans,
  fontSize: 15,
  lineHeight: 1.5,
  color: 'var(--c-text-2)',
  marginTop: 10,
  marginBottom: 0,
  maxWidth: 320,
  '& sup': {
    color: 'var(--champagne)',
  },
});

const Qr = styled('img')({
  width: 104,
  height: 104,
  borderRadius: 12,
  flex: 'none',
  display: 'block',
});

const Badges = styled('div')({
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
  marginTop: 26,
  '@media (max-width: 768px)': {
    width: '100%',
  },
});

const StoreBadge = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 13,
  padding: '13px 22px',
  borderRadius: 14,
  border: '1px solid rgba(233,215,182,0.35)',
  background: 'rgba(0,0,0,0.25)',
  textDecoration: 'none',
  transition: 'border-color 0.15s, background 0.15s',
  '&:hover': {
    borderColor: 'var(--champagne)',
    background: 'rgba(0,0,0,0.4)',
  },
  '@media (max-width: 768px)': {
    flex: 1,
    justifyContent: 'center',
  },
});

const StoreGlyph = styled('span')({
  display: 'grid',
  placeItems: 'center',
  color: '#fff',
  width: 26,
  height: 26,
  flexShrink: 0,
  '& svg': {
    width: 24,
    height: 24,
    display: 'block',
  },
});

const PlayTri = styled('span')({
  width: 0,
  height: 0,
  borderStyle: 'solid',
  borderWidth: '11px 0 11px 18px',
  borderColor: 'transparent transparent transparent var(--champagne)',
  display: 'block',
});

const StoreText = styled('span')({
  display: 'flex',
  flexDirection: 'column',
  lineHeight: 1.15,
});

const StoreTop = styled('span')({
  fontFamily: FONTS.sans,
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: '0.04em',
  color: 'var(--c-text-2)',
  whiteSpace: 'nowrap',
});

const StoreBottom = styled('span')({
  fontFamily: FONTS.serif,
  fontSize: 20,
  fontWeight: 400,
  color: '#fff',
  letterSpacing: '0.005em',
});

const Foot = styled('p')({
  fontFamily: FONTS.sans,
  fontSize: 11.5,
  lineHeight: 1.5,
  color: 'var(--c-text-3)',
  marginTop: 18,
  marginBottom: 0,
  maxWidth: 460,
  '& sup': {
    color: 'var(--champagne)',
  },
});

const AppleGlyph = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.365 1.43c0 1.14-.42 2.2-1.12 2.99-.85.96-2.24 1.7-3.38 1.61-.14-1.1.43-2.27 1.07-3 .73-.83 2.02-1.46 3.1-1.55.02.32.02.63.02.95h.31zM20.5 17.04c-.55 1.27-.82 1.84-1.53 2.96-.99 1.56-2.39 3.5-4.12 3.51-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.05-1.77-4.04-3.33C-.04 16.6-.31 11.2 1.4 8.34c1.21-2.03 3.12-3.21 4.92-3.21 1.83 0 2.98 1 4.49 1 1.47 0 2.36-1 4.48-1 1.6 0 3.3.87 4.51 2.38-3.96 2.17-3.32 7.83.7 9.53z" />
  </svg>
);

const DEFAULT_BONUS_HEAD = (
  <>
    Get 30 days of
    <br />
    bonus shares free
  </>
);

const DEFAULT_BONUS_SUB = (
  <>
    Scan the QR code to download the app for iOS or Android.<sup>*</sup>
  </>
);

const DEFAULT_FOOTNOTE = (
  <>
    <sup>*</sup> Venture Club Free Trial is only available to users who have never previously
    subscribed.
  </>
);

export function AppDownloadSection({
  qrSrc = APP_QR_SRC,
  showAccess = true,
  showApp = true,
  accessText = 'Access is limited. Offerings close when slots fill — ',
  accessEm = 'create a free account',
  signupLabel = 'Sign up to get notified',
  accessTail = ' and get notified when new companies are added.',
  bonusHead = DEFAULT_BONUS_HEAD,
  bonusSub = DEFAULT_BONUS_SUB,
  footnote = DEFAULT_FOOTNOTE,
  trackingPage = 'homepage_v3',
  onSignUpClick,
  appStoreHref = APP_STORE_HREF,
  googlePlayHref = GOOGLE_PLAY_HREF,
}: AppDownloadSectionProps): React.ReactElement {
  const handleStoreClick = (store: 'apple' | 'google') => {
    try {
      trackEvent('clicked_mobile_download_button', {
        page: trackingPage,
        surface: 'app_download',
        store,
      });
    } catch (e) {
      Sentry.captureException(e, { tags: { area: trackingPage } });
    }
  };

  const handleSignUpClick = () => {
    try {
      trackEventWithGTM('clicked_homepage_v3_sign_up', {
        page: trackingPage,
        surface: 'app_download_access',
      });
    } catch (e) {
      Sentry.captureException(e, { tags: { area: trackingPage } });
    }
    onSignUpClick?.();
  };

  const isSolo = !showAccess || !showApp;

  return (
    <Row
      className={isSolo ? 'se-cta-row se-cta-row-solo' : 'se-cta-row'}
      data-solo={isSolo ? 'true' : 'false'}
      data-app={showApp ? 'true' : 'false'}
      data-cy="app-download-section"
    >
      {showAccess ? (
        <Access className="se-cta-access" data-cy="app-access">
          <AccessText className="se-cta-access-text" data-cy="app-access-text">
            {accessText}
            <em>{accessEm}</em>
            {accessTail}
          </AccessText>
          <AccessActions className="se-cta-actions">
            <SignupBtn
              type="button"
              className="se-btn-solid"
              data-cy="app-access-signup"
              onClick={handleSignUpClick}
            >
              {signupLabel}
            </SignupBtn>
          </AccessActions>
        </Access>
      ) : null}
      {showApp ? (
        <App className="se-cta-app">
          <Bonus className="se-app-bonus" data-cy="app-bonus">
            <div className="se-app-bonus-copy">
              <BonusHead className="se-app-bonus-head" data-cy="app-bonus-head">
                {bonusHead}
              </BonusHead>
              <BonusSub className="se-app-bonus-sub">{bonusSub}</BonusSub>
            </div>
            <Qr
              className="se-app-qr"
              src={withSe3AssetPrefix(qrSrc)}
              alt="QR code to download the StartEngine app"
              data-cy="app-qr"
            />
          </Bonus>

          <Badges className="se-app-badges" data-cy="app-badges">
            <StoreBadge
              className="se-store-badge"
              href={appStoreHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleStoreClick('apple')}
              data-cy="app-badge-apple"
            >
              <StoreGlyph className="se-store-glyph">{AppleGlyph}</StoreGlyph>
              <StoreText className="se-store-text">
                <StoreTop className="se-store-top">Download on the</StoreTop>
                <StoreBottom className="se-store-bottom">App Store</StoreBottom>
              </StoreText>
            </StoreBadge>

            <StoreBadge
              className="se-store-badge"
              href={googlePlayHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleStoreClick('google')}
              data-cy="app-badge-google"
            >
              <StoreGlyph className="se-store-glyph">
                <PlayTri className="se-play-tri" />
              </StoreGlyph>
              <StoreText className="se-store-text">
                <StoreTop className="se-store-top">Get it on</StoreTop>
                <StoreBottom className="se-store-bottom">Google Play</StoreBottom>
              </StoreText>
            </StoreBadge>
          </Badges>

          {footnote ? (
            <Foot className="se-app-foot" data-cy="app-foot">
              {footnote}
            </Foot>
          ) : null}
        </App>
      ) : null}
    </Row>
  );
}

export default AppDownloadSection;
